import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { ArrowLeft, TableIcon, Map, Monitor, FileText, MoreHorizontal } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../../components/ui/custom-dropdown-menu';
import { WeldDataMainTable } from './WeldDataMainTable';
import { StatusConfigurationModal } from './StatusConfigurationModal';
import { DeviceManagementModal } from './DeviceManagementModal';
import type { DashboardProject } from '../../../types/project';

interface WeldDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  selectedPipe: any;
  selectedPipeSize: number;
  project: DashboardProject;
  statusCalculationMethod: string;
  filteredWeldIds: string[];
  assemblyNumber: string;
  onFilteredDataChange?: (filteredWpsNumbers: string[]) => void;
  onGenerateReport?: () => void;
  canReport?: boolean;
  // Removed onData360ProjectIdChange - no longer needed
}

export const WeldDataModal: React.FC<WeldDataModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  selectedPipe,
  selectedPipeSize,
  project,
  statusCalculationMethod,
  filteredWeldIds,
  assemblyNumber,
  onFilteredDataChange,
  onGenerateReport,
  canReport = true,
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  const [isStatusConfigurationModalOpen, setIsStatusConfigurationModalOpen] = useState(false);
  const [isDeviceManagementModalOpen, setIsDeviceManagementModalOpen] = useState(false);
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0">
        <div className="flex flex-col h-full">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">
                  Weld Data - {assemblyNumber}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedPipe?.displayName || selectedPipe?.pipeName || 'Pipe Data'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="inline-flex bg-page-bg border border-[#E2E5EA] rounded-lg p-[3px]">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 flex items-center gap-1.5 ${
                    viewMode === 'table'
                      ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                      : 'text-heritage-grey hover:text-blueprint-blue'
                  }`}
                >
                  <TableIcon className="h-3.5 w-3.5" />
                  Table
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('map')}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 flex items-center gap-1.5 ${
                    viewMode === 'map'
                      ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                      : 'text-heritage-grey hover:text-blueprint-blue'
                  }`}
                >
                  <Map className="h-3.5 w-3.5" />
                  Map
                </button>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors">
                  <MoreHorizontal className="h-4 w-4 text-text-grey" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" disablePortal>
                  {canReport && (
                    <DropdownMenuItem onClick={() => onGenerateReport?.()}>
                      <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                      Generate Report
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setIsStatusConfigurationModalOpen(true)}>
                    <Monitor className="h-4 w-4 mr-2" />
                    Status Configuration
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsDeviceManagementModalOpen(true)}>
                    <Monitor className="h-4 w-4 mr-2" />
                    Device Management
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto p-4 pb-20">
              <WeldDataMainTable
                projectId={projectId}
                projectName={projectName}
                project={project}
                selectedPipeSize={selectedPipeSize}
                onFilteredDataChange={onFilteredDataChange}
                onGenerateReport={onGenerateReport}
                // Removed onData360ProjectIdChange prop
                selectedPipe={selectedPipe}
                statusCalculationMethod={statusCalculationMethod}
                filteredWeldIds={filteredWeldIds}
                viewMode={viewMode}
                canReport={canReport}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      
      {/* Status Configuration Modal */}
      <StatusConfigurationModal
        project={project}
        selectedPipeSize={selectedPipeSize}
        selectedPipe={selectedPipe}
        isOpen={isStatusConfigurationModalOpen}
        onClose={() => setIsStatusConfigurationModalOpen(false)}
      />

      {/* Device Management Modal */}
      <DeviceManagementModal
        isOpen={isDeviceManagementModalOpen}
        onClose={() => setIsDeviceManagementModalOpen(false)}
        assemblyNumber={assemblyNumber}
        project={project}
      />
    </Dialog>
  );
};